import { ApolloClient, useApolloClient } from '@apollo/client';
import * as React from 'react';

import type { Host, Xinglet, XingletFunction } from '@xing-com/crate-xinglet';
import type { Runtime } from '@xing-com/crate-xinglet/internal';

import { BrowserRoot } from './browser/root';
import type { RootComponentType } from './root';
import type { ServerRoot } from './server/root';
import type { HopsUploadRequestMutationVariables } from './upload-request-mutation.gql-types';

function importRoot(runtime: Runtime): RootComponentType {
  if (runtime.isServer) {
    const { value: module } = runtime.loadModule<{
      ServerRoot: typeof ServerRoot;
    }>('@xing-com/crate-core-hops-environment', 'server-root');

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return module!.ServerRoot;
  } else {
    return BrowserRoot;
  }
}

export interface HopsEnvironmentExtension {
  executeCommand:
    | XingletFunction<
        HopsEnvironment,
        '@xing-com/crate-core-hops-environment.apolloClient'
      >
    | XingletFunction<
        HopsEnvironment,
        '@xing-com/crate-core-hops-environment.uploadFile'
      >;
}

export type HopsEnvironmentProps = React.PropsWithChildren<{
  basePath?: string;
}>;

export default class HopsEnvironment implements Xinglet {
  private client?: ApolloClient<unknown>;

  public '@xing-com/crate-core-hops-environment.apolloClient'():
    | ApolloClient<unknown>
    | undefined {
    return this.client;
  }

  public async '@xing-com/crate-core-hops-environment.uploadFile'(
    application: HopsUploadRequestMutationVariables['application'],
    file: File,
    errorHandler?: (id: string) => void
  ): Promise<string | null> {
    if (!this.client) {
      throw new Error('No upload outside of hops environment');
    }
    return await (
      await import('./upload')
    ).uploadFile(this.client, application, file, errorHandler);
  }

  public getComponent(
    host: Host,
    runtime: Runtime
  ): React.ComponentType<HopsEnvironmentProps> {
    const Root = importRoot(runtime);

    return ({ basePath = '/', children }) => {
      this.client = useApolloClient();

      return <Root basePath={basePath}>{children}</Root>;
    };
  }
}
